import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Topbar from '../components/layout/Topbar';
import { authAPI, usersAPI } from '../services/api';
import styles from './Users.module.css';
import dashboardStyles from './Dashboard.module.css';

const Users = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', role: 'lecteur' });
  const [editFormData, setEditFormData] = useState({ role: 'lecteur' });

  const roleLabels = {
    superadmin: 'Superadmin',
    administrateur: 'Administrateur',
    collaborateur: 'Collaborateur',
    lecteur: 'Lecteur'
  };

  const roleColors = {
    superadmin: '#dc2626',
    administrateur: '#2563eb',
    collaborateur: '#7c3aed',
    lecteur: '#059669'
  };

  // Fetch user and data on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await authAPI.getMe();
        setUser(userData);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    loadUser();
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, usersData] = await Promise.all([
        usersAPI.getStats(),
        usersAPI.getAll()
      ]);
      setStats(statsData);
      setUsers(usersData);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.password) {
      setError('Email et mot de passe requis');
      return;
    }

    try {
      await usersAPI.create(formData.email, formData.password, formData.role);
      setShowCreateModal(false);
      setFormData({ email: '', password: '', role: 'lecteur' });
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateUser = async () => {
    try {
      await usersAPI.update(selectedUser.id, {
        role: editFormData.role
      });
      setShowEditModal(false);
      setSelectedUser(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${email} ?`)) {
      try {
        await usersAPI.delete(userId);
        await fetchData();
      } catch (err) {
        setError(err.response?.data?.detail || 'Erreur lors de la suppression');
      }
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditFormData({ role: user.role });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className={dashboardStyles.dashboardContainer}>
        <Sidebar />
        <div className={dashboardStyles.mainContent}>
          <Topbar user={user} />
          <div className={dashboardStyles.contentArea}>
            <div className={styles.loading}>Chargement...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={dashboardStyles.dashboardContainer}>
      <Sidebar />
      <div className={dashboardStyles.mainContent}>
        <Topbar user={user} />
        <div className={dashboardStyles.contentArea}>
          <div className={styles.container}>
      <h1>Gestion des Utilisateurs</h1>

      {error && <div className={styles.error}>{error}</div>}

      {/* Stats Cards */}
      {stats && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Superadmins</div>
            <div className={styles.statValue} style={{ color: roleColors.superadmin }}>
              {stats.superadmin}
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Administrateurs</div>
            <div className={styles.statValue} style={{ color: roleColors.administrateur }}>
              {stats.administrateur}
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Collaborateurs</div>
            <div className={styles.statValue} style={{ color: roleColors.collaborateur }}>
              {stats.collaborateur}
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Lecteurs</div>
            <div className={styles.statValue} style={{ color: roleColors.lecteur }}>
              {stats.lecteur}
            </div>
          </div>
        </div>
      )}

      {/* Create User Button */}
      <div className={styles.header}>
        <h2>Liste des Utilisateurs</h2>
        <button className={styles.createBtn} onClick={() => setShowCreateModal(true)}>
          + Ajouter un utilisateur
        </button>
      </div>

      {/* Users Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Rôle</th>
              <th>Date de création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>
                  <span
                    className={styles.roleBadge}
                    style={{ backgroundColor: roleColors[user.role] }}
                  >
                    {roleLabels[user.role]}
                  </span>
                </td>
                <td>{new Date(user.created_at).toLocaleDateString('fr-FR')}</td>
                <td className={styles.actions}>
                  <button
                    className={styles.editBtn}
                    onClick={() => openEditModal(user)}
                    title="Modifier le rôle"
                  >
                    ✎
                  </button>
                  {user.role !== 'superadmin' && (
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDeleteUser(user.id, user.email)}
                      title="Supprimer"
                    >
                      🗑
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2>Ajouter un utilisateur</h2>
            <div className={styles.formGroup}>
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="utilisateur@example.com"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Mot de passe</label>
              <input
                type="password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                placeholder="Entrez un mot de passe"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Rôle</label>
              <select
                value={formData.role}
                onChange={e => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="lecteur">Lecteur</option>
                <option value="collaborateur">Collaborateur</option>
                <option value="administrateur">Administrateur</option>
              </select>
            </div>
            <div className={styles.modalButtons}>
              <button className={styles.cancelBtn} onClick={() => setShowCreateModal(false)}>
                Annuler
              </button>
              <button className={styles.submitBtn} onClick={handleCreateUser}>
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2>Modifier {selectedUser.email}</h2>
            <div className={styles.formGroup}>
              <label>Rôle</label>
              <select
                value={editFormData.role}
                onChange={e => setEditFormData({ role: e.target.value })}
              >
                <option value="lecteur">Lecteur</option>
                <option value="collaborateur">Collaborateur</option>
                <option value="administrateur">Administrateur</option>
              </select>
            </div>
            <div className={styles.modalButtons}>
              <button className={styles.cancelBtn} onClick={() => setShowEditModal(false)}>
                Annuler
              </button>
              <button className={styles.submitBtn} onClick={handleUpdateUser}>
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
